import { useEffect, useMemo, useRef, useState } from "react";
import { NetworkStatus, useQuery } from "@apollo/client";
import _ from "lodash";
import { InfrastructureSelectNodeList } from "../graphql/queries/infrastructureSelectList";
import { InfrastructureSelectRootNodeList } from "../graphql/queries/infrastructureSelectRootNodeList";
import { nodeBrowserPaginationLimit } from "../config/paginationLimit";
import { decidePinnedSelection, PinnedSelectionDecision } from "../utils/pinnedSelection";

const SEARCH_DEBOUNCE_MS = 300;
// A single character practically never narrows anything and just adds a
// round-trip on every keystroke — stay in browse mode until it's usable.
const MIN_SEARCH_LENGTH = 2;

export type InfrastructureNode = {
    id: string;
    key: string;
    title?: string;
    assetCount?: number | null;
    childrenNodeCount?: number | null;
    nodeType?: { key?: string | null; name?: string | null } | null;
    pathNodes: any[];
};

type Params = {
    /** Current browsing level; null = root (company/client list). */
    parentNodeId: string | null;
    includeTrash?: boolean;
    /** Already-resolved selected node (from the single-node lookup query), pinned as the first row when it is not on the first page. */
    selectedNode: InfrastructureNode | null;
    normalizedValue: string | null;
};

/**
 * Shared data layer for InfrastructureBrowser/InfrastructureSelect: paginated
 * + server-searched children/root list. A selected node that is not on the
 * first page of its own level is pinned as the first row so it doesn't need to
 * be re-found by scrolling or searching — like the installed base tree columns,
 * decided once per level/search/selection, never because of a click (a clicked
 * node is loaded, so it stays where it was picked).
 */
export function useInfrastructureNodeList({ parentNodeId, includeTrash, selectedNode, normalizedValue }: Params) {
    const isRoot = parentNodeId === null;

    const [searchInput, setSearchInput] = useState("");
    const [searchQuery, setSearchQuery] = useState("");

    const debouncedSetSearchQuery = useMemo(
        () => _.debounce((query: string) => setSearchQuery(query), SEARCH_DEBOUNCE_MS),
        []
    );
    useEffect(() => () => debouncedSetSearchQuery.cancel(), [debouncedSetSearchQuery]);

    // Reset search when the browsed level changes.
    useEffect(() => {
        debouncedSetSearchQuery.cancel();
        setSearchInput("");
        setSearchQuery("");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [parentNodeId]);

    const onSearchInputChange = (text: string) => {
        setSearchInput(text);
        debouncedSetSearchQuery(text);
    };

    const onClearSearch = () => {
        debouncedSetSearchQuery.cancel();
        setSearchInput("");
        setSearchQuery("");
    };

    const isSearchMode = searchQuery.trim().length >= MIN_SEARCH_LENGTH;
    const searchVar = isSearchMode ? { query: searchQuery.trim(), fields: ["title"] } : undefined;

    const childListQuery = useQuery(InfrastructureSelectNodeList, {
        variables: {
            filter: {
                parentNodeId: { equals: parentNodeId },
                ...(includeTrash === false ? { includeTrash: false } : {}),
            },
            search: searchVar,
            pagination: { start: 0, limit: nodeBrowserPaginationLimit },
        },
        fetchPolicy: "cache-and-network",
        notifyOnNetworkStatusChange: true,
        skip: isRoot,
    });

    const rootListQuery = useQuery(InfrastructureSelectRootNodeList, {
        variables: {
            filter: {},
            search: searchVar,
            pagination: { start: 0, limit: nodeBrowserPaginationLimit },
        },
        fetchPolicy: "cache-and-network",
        notifyOnNetworkStatusChange: true,
        skip: !isRoot,
    });

    const { data, loading: queryLoading, networkStatus, fetchMore } = isRoot ? rootListQuery : childListQuery;

    const listKey = isRoot ? "installedBaseRootNodeList" : "installedBaseNodeList";
    const nodes: InfrastructureNode[] = (data as any)?.[listKey]?.installedBaseNodes ?? [];
    const total: number = (data as any)?.[listKey]?.total ?? 0;

    const loadingMore = networkStatus === NetworkStatus.fetchMore;
    const loading = queryLoading && !loadingMore && nodes.length === 0;

    // Latched per level + search + selection (see decidePinnedSelection):
    // infinite scroll must not flip it, a new selection or level decides anew.
    const pinDecisionRef = useRef<PinnedSelectionDecision | null>(null);
    pinDecisionRef.current = decidePinnedSelection(
        pinDecisionRef.current,
        JSON.stringify({ parentNodeId, search: isSearchMode ? searchQuery.trim() : "" }),
        normalizedValue,
        // Only from the network result: cache-and-network first serves the cached
        // list incl. infinite-scroll pages, which the refetch then cuts back to
        // the first page — deciding on that would leave the selection unpinned
        // and gone.
        data && !queryLoading ? nodes.map((n) => n.id) : undefined
    );
    const pinDecision = pinDecisionRef.current;

    // Pin the selected node as row 0 while its own level is being browsed —
    // only while unfiltered (a search result set shouldn't gain a foreign row)
    // and only once it's confirmed current (id must match the live value, not
    // a stale resolver result from a just-cleared/just-changed selection).
    const selectedPath = selectedNode?.pathNodes ?? [];
    const selectedParentId = selectedPath.length >= 2 ? selectedPath[selectedPath.length - 2].id : null;
    const pinnedSelected =
        pinDecision?.pinned && !isSearchMode && selectedNode && selectedNode.id === normalizedValue && selectedParentId === parentNodeId
            ? selectedNode
            : null;

    const rows: InfrastructureNode[] = pinnedSelected
        ? [pinnedSelected, ...nodes.filter((n) => n.id !== pinnedSelected.id)]
        : nodes;

    // Selection loaded at its real position: callers scroll it into view once
    // per key (it may sit below the fold of the first page).
    const revealSelectedKey =
        pinDecision && !pinDecision.pinned && nodes.some((n) => n.id === normalizedValue)
            ? pinDecision.key
            : null;

    const loadMore = () => {
        if (queryLoading || loadingMore) return;
        if (nodes.length >= total) return;
        fetchMore({
            variables: { pagination: { start: nodes.length, limit: nodeBrowserPaginationLimit } },
            updateQuery: (prev: any, { fetchMoreResult }: any) => {
                if (!fetchMoreResult) return prev;
                return {
                    [listKey]: {
                        ...fetchMoreResult[listKey],
                        installedBaseNodes: [
                            ...(prev?.[listKey]?.installedBaseNodes ?? []),
                            ...(fetchMoreResult[listKey]?.installedBaseNodes ?? []),
                        ],
                    },
                };
            },
        });
    };

    return {
        searchInput,
        onSearchInputChange,
        onClearSearch,
        // Debounced query actually driving the request — not `searchInput`,
        // which echoes every keystroke immediately for responsive typing.
        // Useful to callers that need to reset UI state (e.g. scroll
        // position) only once the result set itself is about to change.
        searchQuery,
        rows,
        total,
        loading,
        loadingMore,
        loadMore,
        revealSelectedKey,
    };
}
